import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Mail, DollarSign, Plus, UserCheck } from 'lucide-react';
import { TasksDueWidget } from '@/components/tasks/TasksDueWidget';
import { useAuth } from '@/lib/auth';
import { KpiCard, PipelineWidget, RecentLeadsWidget, DripWidget, OffersSentWidget } from '@/components/dashboard/CoreWidgets';
import { UpdatesFeedWidget } from '@/components/dashboard/UpdatesFeedWidget';
import { BuyerTiersWidget, TopBuyersWidget, CampaignPerfWidget, UcMatchesWidget } from '@/components/dashboard/BuyerWidgets';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [buyers, setBuyers] = useState({});
  const [offers, setOffers] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    Promise.all([
      API.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {}),
      API.get('/dashboard/buyers').then(r => setBuyers(r.data)).catch(() => {}),
      API.get('/dashboard/offers-sent').then(r => setOffers(r.data)).catch(() => {}),
      API.get('/dashboard/updates', { params: { limit: 15 } }).then(r => setUpdates(r.data.items)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="space-y-6">
        <Skeleton className="h-10 w-56 bg-[#1E293B]" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 rounded-2xl bg-[#1E293B]" />)}</div>
        <Skeleton className="h-72 rounded-2xl bg-[#1E293B]" />
      </div>
    );
  }

  const hour = new Date().getHours();
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const hot = buyers.tiers?.Hot || 0;

  return (
    <div data-testid="dashboard-page" className="relative space-y-6 overflow-x-clip">
      <div className="pointer-events-none absolute -top-24 left-1/3 h-72 w-[40rem] rounded-full bg-[#3B82F6]/10 blur-3xl" />
      <div className="relative flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-300/80">{greet}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</p>
          <h1 className="mt-1 text-4xl font-bold tracking-tight text-white sm:text-5xl">Command Center</h1>
          <p className="mt-1 text-sm text-slate-400">Deals, buyers and outreach in one place</p>
        </div>
        <Button data-testid="new-lead-btn" onClick={() => navigate('/leads')}
          className="h-11 rounded-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] px-6 text-white shadow-[0_0_24px_rgba(99,102,241,0.45)] transition-transform hover:scale-[1.03]">
          <Plus className="mr-2 h-4 w-4" /> New Lead
        </Button>
      </div>

      <div className="relative grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testId="kpi-total-leads" valueTestId="kpi-leads-value" label="Total Leads" value={(stats?.total_leads || 0).toLocaleString()} sub={`${stats?.follow_up_count || 0} need follow-up`} icon={Users} color="#3B82F6" delay={0} />
        <KpiCard testId="kpi-total-buyers" valueTestId="kpi-buyers-value" label="Cash Buyers" value={(buyers.total ?? stats?.total_buyers ?? 0).toLocaleString()} sub={`${hot} hot · +${buyers.new_this_month || 0} this month`} icon={UserCheck} color="#10B981" delay={80} />
        <KpiCard testId="kpi-emails-sent" valueTestId="kpi-emails-value" label="Emails Sent" value={((stats?.total_emails || 0) + (buyers.campaigns?.sent || 0)).toLocaleString()} sub={`${stats?.drip?.emails_sent || 0} drip · ${buyers.campaigns?.sent || 0} campaigns`} icon={Mail} color="#8B5CF6" delay={160} />
        <KpiCard testId="kpi-deal-value" valueTestId="kpi-value-amount" label="Closed Value" value={`$${(stats?.total_deal_value || 0).toLocaleString()}`} sub="lifetime earnings" icon={DollarSign} color="#F59E0B" delay={240} />
      </div>

      <div className="relative grid grid-cols-1 gap-5 lg:grid-cols-12">
        <OffersSentWidget data={offers} onGoalSaved={() => API.get('/dashboard/offers-sent').then(r => setOffers(r.data)).catch(() => {})} />
        <UpdatesFeedWidget items={updates} />
        <div className="lg:col-span-6 [&>*]:h-full"><TasksDueWidget /></div>
        <PipelineWidget pipeline={stats?.pipeline} />
        <BuyerTiersWidget data={buyers} />
        <TopBuyersWidget data={buyers} />
        <RecentLeadsWidget leads={stats?.recent_leads} />
        <UcMatchesWidget data={buyers} />
        <CampaignPerfWidget data={buyers} />
        <DripWidget drip={stats?.drip} />
      </div>
    </div>
  );
}
